import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  CalendarCheck, CheckCircle2, Clock, ShieldCheck, Mail,
  ArrowRight, Sparkles, Headphones, Workflow, LineChart, Stethoscope,
} from "lucide-react";
import { DemoFormFields } from "@/components/landing/DemoForm";
import { FinalCTA } from "@/components/landing/FinalCTA";

/* /book-a-demo — high-conversion editorial layout matching the rest of the site. */

const BENEFITS = [
  { Icon: Workflow,    text: "A tailored walkthrough of your unified inbox & AI workforce." },
  { Icon: Stethoscope, text: "Live look at 2-way Jane App / Juvonno sync — built for your EMR." },
  { Icon: LineChart,   text: "A custom ROI estimate based on your clinic's volume." },
  { Icon: ShieldCheck, text: "No commitment. Plans start at $297/mo. HIPAA-ready out of the box." },
];

const STEPS = [
  { n: "01", title: "You submit the form", body: "Takes under a minute. We confirm by email in seconds." },
  { n: "02", title: "We call within 1 business day", body: "Quick 5-min chat to understand your stack & goals." },
  { n: "03", title: "20-min personalised walkthrough", body: "Live on Zoom. Your EMR, your modules, your data." },
  { n: "04", title: "ROI report + go-live plan", body: "Tailored numbers, integration map, 14-day rollout." },
];

const TRUST = [
  { k: "22",     v: "Clinics live today" },
  { k: "5+ hrs", v: "Saved per week" },
  { k: "99%",    v: "Patient answer rate" },
  { k: "1 / 1",  v: "System · login" },
];

const FAQS = [
  {
    q: "How long is the demo?",
    a: "20 minutes — live on Zoom. We walk through the modules that matter to your clinic and answer questions in real time.",
  },
  {
    q: "Do I need to prep anything?",
    a: "Just your EMR name and a rough sense of monthly patient volume. We'll handle the rest.",
  },
  {
    q: "What does it cost to switch?",
    a: "Plans start at $297/mo. We migrate from your current stack in under 14 days at no extra cost.",
  },
  {
    q: "Will it disturb my current EMR?",
    a: "Never. Clinic OS reads from Jane / Juvonno — it never edits or deletes records. Disconnect anytime.",
  },
];

export default function BookDemo() {
  return (
    <article data-testid="book-demo-page" className="overflow-hidden">

      {/* HERO */}
      <section className="relative overflow-hidden bg-background">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-60" />
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[560px] bg-gradient-to-b from-violet-500/15 via-fuchsia-500/8 to-transparent blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 pt-16 pb-10 sm:px-8 sm:pt-40 sm:pb-20 lg:px-12">
          <div className="grid items-start gap-8 sm:gap-12 lg:grid-cols-12 lg:gap-16">
            {/* LEFT — Pitch */}
            <div className="lg:col-span-7">
              <p className="hidden sm:inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.28em] text-muted-foreground">
                <CalendarCheck className="h-3.5 w-3.5 text-violet-500" />
                Book a demo · 20 minutes
              </p>

              <h1 className="mt-0 sm:mt-6 font-display text-[2.05rem] font-black leading-[1.02] tracking-tight sm:text-5xl lg:text-[4rem]">
                See Clinic OS{" "}
                <span className="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 bg-clip-text italic text-transparent">
                  run your clinic.
                </span>
              </h1>

              <p className="mt-3 sm:mt-6 max-w-xl text-[14px] leading-relaxed text-muted-foreground sm:text-[17px]">
                A tailored 20-minute walkthrough on your real EMR — not a generic deck. You&apos;ll leave with an ROI estimate, an integration map, and a 14-day rollout plan.
              </p>

              {/* Hero trust pills */}
              <div className="mt-5 sm:mt-7 flex flex-wrap gap-x-3 sm:gap-x-5 gap-y-1.5 sm:gap-y-2 text-[11.5px] sm:text-[12.5px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="font-bold text-foreground/85">1 business day</span> · reply time
                </span>
                <span className="hidden h-1 w-1 rounded-full bg-border sm:inline" />
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                  HIPAA-ready · SOC 2 aligned
                </span>
                <span className="hidden h-1 w-1 rounded-full bg-border sm:inline" />
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-violet-500" />
                  No commitment
                </span>
              </div>

              {/* Trust stats — hidden on mobile, the form is right below the hero */}
              <div className="mt-10 hidden grid-cols-2 gap-3 sm:grid sm:grid-cols-4">
                {TRUST.map((t) => (
                  <div
                    key={t.v}
                    className="rounded-2xl border border-border bg-card p-4"
                  >
                    <p className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text font-display text-2xl font-black tracking-tight text-transparent sm:text-3xl">
                      {t.k}
                    </p>
                    <p className="mt-1 text-[11.5px] font-semibold leading-snug text-muted-foreground">{t.v}</p>
                  </div>
                ))}
              </div>

              {/* Founder mini-card — hidden on mobile to reduce hero length */}
              <div className="mt-10 hidden max-w-md overflow-hidden rounded-2xl border border-border bg-card p-5 sm:block">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 font-display text-sm font-black text-white shadow-md ring-1 ring-inset ring-white/15">
                    MP
                  </span>
                  <div>
                    <p className="font-display text-[14px] font-bold leading-snug text-foreground">
                      &ldquo;Eight tools, gone. Patient leaks, gone. One login, one system — and 35% more productive.&rdquo;
                    </p>
                    <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.18em] text-sky-600 dark:text-sky-300">
                      Mathew Puickal · co-founder · OPTSC
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — Form card (sticky on lg) */}
            <div className="lg:col-span-5">
              <div
                data-testid="book-demo-form-card"
                className="lg:sticky lg:top-28 overflow-hidden rounded-3xl border border-border bg-card shadow-2xl"
              >
                <div className="border-b border-border bg-gradient-to-r from-violet-500/10 via-fuchsia-500/8 to-rose-500/10 px-6 py-5">
                  <p className="font-display text-lg font-black tracking-tight">Request your demo</p>
                  <p className="mt-0.5 text-[13px] text-muted-foreground">Takes less than a minute. We reply within 1 business day.</p>
                </div>
                <div className="p-6 sm:p-7">
                  <DemoFormFields />
                </div>
                <div className="border-t border-border bg-secondary/30 px-6 py-3 text-[11px] text-muted-foreground">
                  By submitting, you agree to our{" "}
                  <Link to="/privacy" className="font-semibold text-foreground/80 underline-offset-2 hover:underline">privacy policy</Link>.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT TO EXPECT — 4 steps */}
      <section className="border-t border-border bg-background py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-violet-600 dark:text-violet-400">
              What happens after you submit
            </p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
              Four steps. <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text italic text-transparent">No fluff.</span>
            </h2>
          </div>

          <ol className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <motion.li
                key={s.n}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                data-testid={`book-demo-step-${i}`}
                className="relative rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                <span className="font-display text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-400">
                  STEP · {s.n}
                </span>
                <p className="mt-2 font-display text-[16px] font-black leading-tight tracking-tight">{s.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{s.body}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHY BOOK + FAQ */}
      <section className="border-t border-border py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            {/* LEFT — Benefits */}
            <div className="lg:col-span-6">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-violet-600 dark:text-violet-400">
                What you&apos;ll walk away with
              </p>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
                A demo built for{" "}
                <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text italic text-transparent">your clinic</span>.
              </h2>

              <ul className="mt-8 space-y-4">
                {BENEFITS.map((b, i) => (
                  <motion.li
                    key={b.text}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                    className="flex items-start gap-4"
                  >
                    <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/15 to-fuchsia-500/10 text-violet-600 dark:text-violet-300">
                      <b.Icon className="h-5 w-5" />
                    </span>
                    <span className="pt-1.5 text-[15px] leading-relaxed text-foreground/85">{b.text}</span>
                  </motion.li>
                ))}
              </ul>

              {/* Contact rail — email only */}
              <div className="mt-12 rounded-2xl border border-border bg-card p-5">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
                  Prefer a different channel?
                </p>
                <a
                  href="mailto:hello@clinicfirst.ca"
                  data-testid="book-demo-email"
                  className="group mt-4 flex items-center gap-3 rounded-xl border border-border bg-background p-4 transition-colors hover:border-violet-500/40 hover:bg-secondary/40"
                >
                  <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-violet-500/15 text-violet-600 dark:text-violet-300">
                    <Mail className="h-4 w-4" />
                  </span>
                  <span>
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Email us</p>
                    <p className="font-display text-[14.5px] font-bold tracking-tight">hello@clinicfirst.ca</p>
                  </span>
                  <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </a>
              </div>
            </div>

            {/* RIGHT — FAQ */}
            <div className="lg:col-span-6">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-violet-600 dark:text-violet-400">
                Frequently asked
              </p>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
                Quick answers.
              </h2>

              <div className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card">
                {FAQS.map((f, i) => (
                  <details key={f.q} className="group" data-testid={`book-demo-faq-${i}`}>
                    <summary className="flex cursor-pointer items-start gap-3 px-5 py-4 transition-colors hover:bg-secondary/40 [&::-webkit-details-marker]:hidden">
                      <span className="flex-1 font-display text-[14px] font-bold leading-snug tracking-tight">
                        {f.q}
                      </span>
                      <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full border border-border text-muted-foreground transition-transform group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <p className="px-5 pb-5 text-[13.5px] leading-relaxed text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>

              {/* Support nudge */}
              <div className="mt-6 flex items-start gap-3 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] p-5">
                <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
                  <Headphones className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-display text-sm font-black tracking-tight">Already a customer?</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                    Skip the demo — head to{" "}
                    <Link to="/support" className="font-semibold text-foreground/85 underline-offset-2 hover:underline">
                      Support
                    </Link>{" "}
                    for the fastest path to a human.
                    <ArrowRight className="ml-1 inline-block h-3.5 w-3.5 align-text-bottom text-foreground/60" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </article>
  );
}
